import { create } from 'zustand';

import type { Toast, ToastId, ToastType } from './types';
import { createToastId } from './types';

const DEFAULT_DURATION = 5000;
const MAX_TOASTS = 5;

type ToastState = {
  toasts: Toast[];
};

type ToastActions = {
  addToast: (toast: Omit<Toast, 'id'>) => ToastId;
  removeToast: (id: ToastId) => void;
  clearAllToasts: () => void;
};

type ToastStore = ToastState & ToastActions;

export const useToastStore = create<ToastStore>()((set, get) => ({
  toasts: [],

  addToast: (toastOptions) => {
    const id = createToastId();
    const toast: Toast = {
      id,
      ...toastOptions,
    };

    set((state) => {
      const newToasts = [toast, ...state.toasts].slice(0, MAX_TOASTS);
      return { toasts: newToasts };
    });

    if (toast.duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, toast.duration);
    }

    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAllToasts: () => {
    set({ toasts: [] });
  },
}));

type ToastFn = (
  messageKey: string,
  options?: {
    messageParams?: Record<string, string | number>;
    duration?: number;
    dismissible?: boolean;
  }
) => ToastId;

function createToastFn(type: ToastType): ToastFn {
  return (messageKey, options = {}) => {
    const {
      messageParams,
      duration = DEFAULT_DURATION,
      dismissible = true,
    } = options;
    return useToastStore.getState().addToast({
      type,
      messageKey,
      messageParams,
      duration,
      dismissible,
    });
  };
}

export const toast = {
  success: createToastFn('success'),
  error: createToastFn('error'),
  info: createToastFn('info'),
  warning: createToastFn('warning'),
};
